import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUnreadMessages } from '../../services/dashboard';

const UnreadMessages = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let interval = null;
    let timeout = null;
    
    const loadUnreadMessages = async () => {
      // Only fetch if page is visible
      if (document.hidden) {
        return;
      }
      
      try {
        setLoading(true);
        const response = await getUnreadMessages(5);
        if (response && response.success !== false && response.data) {
          setMessages(response.data.messages || []);
          setUnreadCount(response.data.unreadCount || 0);
        }
      } catch (error) {
        console.error('Failed to load unread messages:', error);
        // On error, increase interval to avoid rate limiting
        if (interval) {
          clearInterval(interval);
          // Retry after 2 minutes on error
          timeout = setTimeout(() => {
            interval = setInterval(loadUnreadMessages, 120000); // 2 minutes
          }, 120000);
        }
      } finally {
        setLoading(false);
      }
    };

    // Initial load
    loadUnreadMessages();
    
    // Poll for new messages every 60 seconds (reduced frequency)
    interval = setInterval(loadUnreadMessages, 60000);
    
    // Stop polling when tab is hidden, resume when visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (interval) clearInterval(interval);
        if (timeout) clearTimeout(timeout);
      } else {
        // Resume polling when tab becomes visible
        loadUnreadMessages();
        if (!interval) {
          interval = setInterval(loadUnreadMessages, 60000);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="bg-white rounded-[12px] border border-[#f2f2f2] p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Unread Messages</h3>
        {unreadCount > 0 && (
          <span className="px-2 py-1 bg-emerald-400 text-white text-xs font-medium rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : messages.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm mb-2">No unread messages</p>
          <button
            onClick={() => navigate('/messages')}
            className="text-emerald-400 text-sm font-medium hover:underline"
          >
            Go to Messages
          </button>
        </div>
      ) : (
        <div className="space-y-4 max-h-[400px] overflow-y-auto">
          {messages.map((message) => (
            <div
              key={message.id}
              onClick={() => navigate(`/messages?licenceId=${message.licenceId}`)}
              className="p-3 rounded-lg border border-gray-200 hover:border-emerald-400 cursor-pointer transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    {message.licenceName}
                  </p>
                  <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                    {message.body}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{message.sender}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">
                      {formatTimeAgo(message.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {unreadCount > messages.length && (
            <button
              onClick={() => navigate('/messages')}
              className="w-full py-2 text-emerald-400 text-sm font-medium hover:underline"
            >
              View all messages ({unreadCount})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default UnreadMessages;


